import { useCallback, useRef, useState } from 'react'
import { Button, Space, Switch, Tag, Typography, message } from 'antd'
import { ArrowLeftOutlined, DownloadOutlined, FilePdfOutlined, SafetyCertificateOutlined } from '@ant-design/icons'
import { api, formatApiError } from '../api'
import { PdfJsScrollViewer } from './PdfJsScrollViewer'
import './DocDesensitizeViewer.css'
import './PdfJsScrollViewer.css'

const { Text } = Typography

function maskedDownloadName(fileName: string): string {
  const base = fileName.trim() || 'document.pdf'
  const withExt = /\.pdf$/i.test(base) ? base : `${base}.pdf`
  return `脱敏-${withExt}`
}

type Props = {
  jobId: number
  fileName: string
  hitCount: number
  truncated?: boolean
  onBack: () => void
}

export function DocDesensitizeViewer({ jobId, fileName, hitCount, truncated, onBack }: Props) {
  const leftScrollRef = useRef<HTMLDivElement>(null)
  const rightScrollRef = useRef<HTMLDivElement>(null)
  const syncing = useRef(false)
  const [syncScroll, setSyncScroll] = useState(true)
  const [downloading, setDownloading] = useState(false)

  const sourceUrl = `/api/public/desensitize/jobs/${jobId}/source`
  const maskedUrl = `/api/public/desensitize/jobs/${jobId}/masked`

  const downloadMasked = () => {
    setDownloading(true)
    api
      .get(`/public/desensitize/jobs/${jobId}/masked`, { responseType: 'blob' })
      .then((res) => {
        const url = URL.createObjectURL(res.data)
        const a = document.createElement('a')
        a.href = url
        a.download = maskedDownloadName(fileName)
        a.click()
        URL.revokeObjectURL(url)
      })
      .catch((e) => message.error(formatApiError(e, '下载脱敏文件失败')))
      .finally(() => setDownloading(false))
  }

  const syncFrom = useCallback(
    (source: 'left' | 'right') => {
      if (!syncScroll) return
      const a = leftScrollRef.current
      const b = rightScrollRef.current
      if (!a || !b) return
      syncing.current = true
      if (source === 'left') b.scrollTop = a.scrollTop
      else a.scrollTop = b.scrollTop
      requestAnimationFrame(() => {
        syncing.current = false
      })
    },
    [syncScroll],
  )

  return (
    <div className="dsv-root">
      <div className="dsv-toolbar">
        <Space wrap size="middle">
          <Button icon={<ArrowLeftOutlined />} onClick={onBack}>
            返回
          </Button>
          <Text strong ellipsis style={{ maxWidth: 280 }}>
            {fileName}
          </Text>
          <Tag color="green">命中 {hitCount} 处</Tag>
          {truncated ? <Tag color="orange">比对数据已截断</Tag> : null}
        </Space>
        <Space wrap size="middle">
          <Switch
            checked={syncScroll}
            onChange={setSyncScroll}
            checkedChildren="同屏滚动"
            unCheckedChildren="独立滚动"
          />
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            loading={downloading}
            onClick={() => downloadMasked()}
          >
            下载脱敏文件
          </Button>
        </Space>
      </div>

      <div className="dsv-dual">
        <div className="dsv-pane">
          <div className="dsv-pane-head">
            <Tag icon={<FilePdfOutlined />} color="blue">
              源文件
            </Tag>
            <Text type="secondary" ellipsis>
              {fileName}
            </Text>
          </div>
          <div className="dsv-pane-body">
            <PdfJsScrollViewer
              url={sourceUrl}
              scrollRef={leftScrollRef}
              onScroll={() => !syncing.current && syncFrom('left')}
            />
          </div>
        </div>

        <div className="dsv-pane-divider" aria-hidden />

        <div className="dsv-pane">
          <div className="dsv-pane-head dsv-pane-head--right">
            <Tag icon={<SafetyCertificateOutlined />} color="green">
              脱敏后
            </Tag>
            <Text type="secondary" ellipsis>
              {fileName}
            </Text>
          </div>
          <div className="dsv-pane-body">
            <PdfJsScrollViewer
              url={maskedUrl}
              scrollRef={rightScrollRef}
              onScroll={() => !syncing.current && syncFrom('right')}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
